import type { AccessRuleInput } from '@qualicloud/contracts';
import { useMemo } from 'react';
import {
  buildPermissionMatrix,
  detectConfigurationWarnings,
  toDomainRule,
  type SubjectDescriptor,
} from '@/lib/effective-permissions';
import { IconAlertTriangle } from '@/design-system';

export interface ConfigurationWarningsProps {
  readonly rules: readonly AccessRuleInput[];
  readonly subjects: readonly SubjectDescriptor[];
  readonly approvalPolicy: string;
  readonly quorumSize: number | null;
}

/**
 * OS GUARDAS DA CONFIGURAÇÃO DE ACESSO — o que sobrou da matriz.
 *
 * A tabela "Permissão efetiva por colaborador" foi removida: era uma grade de
 * seis colunas por colaborador, e numa organização de trinta pessoas ocupava a
 * tela inteira para responder uma pergunta que quase ninguém faz duas vezes.
 *
 * O QUE NÃO PODIA SAIR COM ELA são os três avisos que só ela mostrava, e que
 * pegam falhas SILENCIOSAS — o tipo de erro que não dá mensagem, não impede o
 * salvamento e só aparece semanas depois, quando alguém pergunta por que a
 * versão nunca foi aprovada:
 *
 *   · nenhum colaborador enxerga o documento — ele existe e é invisível na
 *     Lista Mestra para todo mundo;
 *   · nenhum colaborador tem a permissão Aprovar — a versão fica pendente
 *     para sempre, sem que nada na tela diga isso;
 *   · o quórum exige mais aprovações do que existem aprovadores — idem.
 *
 * A lógica é a MESMA de antes, na mesma função pura
 * (`detectConfigurationWarnings`), com os mesmos testes: `[URS-ACL-009]`,
 * `[URS-ACL-010]` e `[URS-ACL-011]` seguem sendo a evidência do REQ-05
 * (§11.10(d)) — o que mudou foi só onde o resultado dela aparece.
 *
 * Não é cartão nem tabela: são linhas, e só existem quando há o que dizer. Em
 * configuração sadia, esta seção não ocupa um pixel.
 */
export function ConfigurationWarnings({
  rules,
  subjects,
  approvalPolicy,
  quorumSize,
}: ConfigurationWarningsProps) {
  const avisos = useMemo(() => {
    if (rules.length === 0 || subjects.length === 0) return [];
    const rows = buildPermissionMatrix(rules.map(toDomainRule), subjects);
    return detectConfigurationWarnings({ rows, approvalPolicy, quorumSize });
  }, [rules, subjects, approvalPolicy, quorumSize]);

  if (avisos.length === 0) return null;

  return (
    <ul aria-label="Avisos da configuração de acesso" className="flex flex-col gap-2">
      {avisos.map((aviso) => (
        <li
          key={aviso}
          role="alert"
          className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5 text-sm text-warning-content"
        >
          <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>{aviso}</span>
        </li>
      ))}
    </ul>
  );
}
